import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useIsMutating, useQueryClient } from '@tanstack/react-query';

import { IconArchive } from '@components/icons/archive';
import { IconCopy } from '@components/icons/copy';
import { IconDownload } from '@components/icons/download';
import { IconEdit } from '@components/icons/edit';
import { IconMove } from '@components/icons/move';
import { IconShare } from '@components/icons/share';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import type { ListCardProps } from '@components/list-card';
import { useToaster } from '@components/toaster';

import {
  jobQueryOptions,
  useArchiveJob,
  useDuplicateJob,
  useJobActionFailureSubtitle,
  useMoveToDrafts,
  usePublishJob,
  useUnpublishJob,
} from 'api/jobs/hooks';
import { reportRequestError } from 'api/request';
import { useFavoriteActionItem } from 'components/navigation/favorites';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { visibleJobActions } from 'helpers/job-actions';
import { jobPublishBlockers } from 'helpers/job-publish';
import { useStore } from 'models/Provider';
import { useExportApplicantsSidebar } from 'pages/Job/sidebars/export-applicants-sidebar';
import type { JobListRow } from 'types/jobs';

import { jobCounts, jobTitle, Translate } from './job-display';

type ActionItems = NonNullable<ListCardProps['actionItems']>;

const JOB_ACTION_KEYS: unknown[] = [
  'publish-job',
  'un-publish-job',
  'archive-job',
  'draft-job',
  'duplicate-job',
];

export const useJobActions = () => {
  const { t, jobStore, current } = useStore();
  const translate = t as Translate;
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  const { showSuccess, showError } = useToaster();
  const location = useLocation();
  const navigate = useNavigate();
  const getFavoriteAction = useFavoriteActionItem('job');
  const showExportApplicantsSidebar = useExportApplicantsSidebar();
  const queryClient = useQueryClient();
  // One set of mutations serves every row, so each row reads its own in-flight state from
  // the mutation cache instead of the shared `isLoading`/`variables`.
  const mutationsInFlight = useIsMutating({
    predicate: (mutation) => JOB_ACTION_KEYS.includes(mutation.options.mutationKey?.[0]),
  });
  const [checkingPublish, setCheckingPublish] = useState<ReadonlySet<number>>(new Set());
  const actingJobIds = useMemo(() => {
    const byKey = new Map<string, Set<number>>();
    if (mutationsInFlight === 0) return byKey;
    queryClient
      .getMutationCache()
      .findAll({ predicate: (mutation) => mutation.state.status === 'loading' })
      .forEach((mutation) => {
        const key = mutation.options.mutationKey?.[0];
        const jobId = (mutation.state.variables as { jobId?: number } | undefined)?.jobId;
        if (typeof key !== 'string' || jobId === undefined) return;
        byKey.set(key, (byKey.get(key) ?? new Set()).add(jobId));
      });
    return byKey;
  }, [queryClient, mutationsInFlight]);

  const { mutateAsync: publishAsync } = usePublishJob();
  const { mutate: unpublish } = useUnpublishJob();
  const { mutateAsync: archiveAsync } = useArchiveJob();
  const { mutate: moveToDrafts } = useMoveToDrafts();
  const { mutate: duplicate } = useDuplicateJob();
  const failureSubtitle = useJobActionFailureSubtitle();

  return useCallback(
    (job: JobListRow, hasManageAccess: boolean): ActionItems => {
      const title = jobTitle({
        job,
        defaultLanguage: current?.info?.defaultLanguage,
        t: translate,
      });
      const visible = visibleJobActions({ job, hasManageAccess, hasRolePermission });
      const isActing = (mutationKey: string) => actingJobIds.get(mutationKey)?.has(job.id) ?? false;

      const openPublishedModal = () =>
        showModal('JOB_PUBLISHED', {
          _container: {
            closeOnOverlayClickOrEsc: false,
            autoWidth: true,
            minWidth: '500px',
          },
          job,
          t,
        });

      const publishIfComplete = async () => {
        let blockers = jobPublishBlockers(job.languages ?? [], translate);
        if (blockers.length > 0) {
          // The card can trail a recent edit, so recheck the job before refusing.
          setCheckingPublish((ids) => new Set(ids).add(job.id));
          const fresh = await queryClient
            .fetchQuery({ ...jobQueryOptions(job.id), staleTime: 0 })
            .catch((error) => {
              reportRequestError(error);
              return undefined;
            })
            .finally(() =>
              setCheckingPublish((ids) => {
                const next = new Set(ids);
                next.delete(job.id);
                return next;
              }),
            );
          if (fresh) blockers = jobPublishBlockers(fresh.languages, translate);
        }
        if (blockers.length > 0) {
          blockers.forEach((message) => showError(message, { subtitle: title }));
          return;
        }

        // Awaited: starting a second publish detaches the shared mutation's first observer.
        await publishAsync({ jobId: job.id, title })
          .then(openPublishedModal)
          // The hook's onError shows the toast; this only stops the rejection escaping.
          .catch(() => undefined);
      };

      const items: ActionItems = [];

      if (visible.has('edit')) {
        items.push({
          id: 'edit',
          icon: <IconEdit />,
          label: translate('job.options.edit'),
          onSelect: () =>
            navigate(`/job/${job.id}/edit/description`, { state: { from: location.pathname } }),
        });
      }

      if (visible.has('duplicate')) {
        items.push({
          id: 'duplicate',
          icon: <IconCopy />,
          label: translate('job.options.duplicate'),
          isDisabled: isActing('duplicate-job'),
          onSelect: () =>
            duplicate({
              jobId: job.id,
              onSuccess: () => showSuccess(translate('job.duplicate.success'), { subtitle: title }),
              onError: (error) =>
                showError(translate('job.duplicate.error'), {
                  subtitle: failureSubtitle(error, title),
                }),
            }),
        });
      }

      if (visible.has('export')) {
        items.push({
          id: 'export',
          icon: <IconDownload />,
          label: translate('job.options.export'),
          onSelect: () =>
            showExportApplicantsSidebar({
              job: {
                id: job.id,
                languages: job.languages,
                numApplications: jobCounts(job).total,
              },
            }),
        });
      }

      const favoriteItem = getFavoriteAction(job.id);
      if (favoriteItem) {
        items.push({
          id: 'favorite',
          icon: favoriteItem.icon,
          label: favoriteItem.label,
          onSelect: favoriteItem.onSelect,
        });
      }

      if (visible.has('share')) {
        items.push({
          id: 'share',
          icon: <IconShare />,
          label: translate('job.options.share'),
          onSelect: () => {
            // ShareJobModal is still MobX-driven and calls job.load() itself, so it needs the
            // MST node. `addOrUpdate` always puts, so the node is there on the next line.
            jobStore.addOrUpdate({ id: job.id });
            showModal('SHARE_JOB_MODAL', {
              _container: {
                autoWidth: false,
                closeOnOverlayClickOrEsc: false,
              },
              job: jobStore.items.get(String(job.id)),
            });
          },
        });
      }

      if (hasManageAccess) {
        items.push('separator');
      }

      if (visible.has('unpublish')) {
        items.push({
          id: 'unpublish',
          icon: <IconVisibilityHidden />,
          label: translate('job.options.unpublish'),
          isDisabled: isActing('un-publish-job'),
          onSelect: () => unpublish({ jobId: job.id, title }),
        });
      }

      if (visible.has('publish')) {
        items.push({
          id: 'publish',
          icon: <IconVisibilityShown />,
          label: translate('job.options.publish'),
          isDisabled: isActing('publish-job') || checkingPublish.has(job.id),
          onSelect: publishIfComplete,
        });
      }

      if (visible.has('archive')) {
        items.push({
          id: 'archive',
          icon: <IconArchive />,
          label: translate('job.options.archive'),
          onSelect: () =>
            showModal('CONFIRM_MODAL', {
              _container: { minWidth: '200px', autoWidth: true },
              data: { id: job.id },
              // Resolves false rather than rejecting, so a failed archive leaves the modal
              // open to retry. The mutation's onError shows the toast.
              confirm: async (id: string | number) => {
                try {
                  await archiveAsync({ jobId: Number(id), title });
                  return true;
                } catch {
                  return false;
                }
              },
              buttonIcon: <IconArchive />,
              headerText: translate('job.archive.modal.header'),
              mainText: translate('job.archive.modal.text', title),
              confirmText: translate('job.archive.modal.button'),
            }),
        });
      }

      if (visible.has('drafts')) {
        items.push({
          id: 'drafts',
          icon: <IconMove />,
          label: translate('job.options.drafts'),
          isDisabled: isActing('draft-job'),
          onSelect: () => moveToDrafts({ jobId: job.id, title }),
        });
      }

      return items;
    },
    [
      actingJobIds,
      checkingPublish,
      current,
      translate,
      t,
      hasRolePermission,
      queryClient,
      showModal,
      showSuccess,
      showError,
      navigate,
      location.pathname,
      getFavoriteAction,
      showExportApplicantsSidebar,
      jobStore,
      publishAsync,
      unpublish,
      archiveAsync,
      moveToDrafts,
      duplicate,
      failureSubtitle,
    ],
  );
};
